<template>
  <div class="service-faq">
    <!-- FAQ Categories -->
    <div class="flex flex-wrap justify-center gap-2 mb-8">
      <Button
        v-for="category in faqCategories"
        :key="category.id"
        :variant="activeCategory === category.id ? 'primary' : 'ghost'"
        size="small"
        @click="setActiveCategory(category.id)"
      >
        <component :is="getCategoryIcon(category.icon)" class="w-4 h-4 mr-1" aria-hidden="true" />
        {{ category.name }}
      </Button>
    </div>

    <!-- FAQ Items -->
    <div class="max-w-4xl mx-auto">
      <div class="space-y-4">
        <div
          v-for="faq in filteredFAQs"
          :key="faq.id"
          class="faq-item border border-gray-200 rounded-lg overflow-hidden"
        >
          <button
            @click="toggleFAQ(faq.id)"
            class="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
            :aria-expanded="openFAQs.includes(faq.id)"
          >
            <span class="font-medium text-gray-900 pr-4">{{ faq.question }}</span>
            <span 
              class="flex-shrink-0 w-6 h-6 flex items-center justify-center transition-transform duration-200"
              :class="openFAQs.includes(faq.id) ? 'rotate-180' : ''"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          
          <div
            v-show="openFAQs.includes(faq.id)"
            class="px-6 pb-4 bg-gray-50 border-t border-gray-100"
          >
            <div class="prose prose-sm max-w-none pt-3 text-gray-700">
              <!-- ✅ CRIT-002 FIX: Sanitized HTML rendering (ADR-154) -->
              <!-- Defense-in-Depth: Even though FAQ data is hardcoded, sanitize for security best practices -->
              <div v-html="sanitizeHtml(faq.answer, {}, 'default')"></div>
              
              <!-- Related Links -->
              <div v-if="faq.relatedLinks && faq.relatedLinks.length > 0" class="mt-4 pt-3 border-t border-gray-200">
                <h4 class="text-sm font-semibold text-gray-900 mb-2">Enlaces relacionados:</h4>
                <ul class="space-y-1">
                  <li v-for="link in faq.relatedLinks" :key="link.url">
                    <a 
                      :href="link.url" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      class="text-teal-600 hover:text-teal-800 text-sm underline"
                    >
                      {{ link.text }} ↗
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No results message -->
      <div v-if="filteredFAQs.length === 0" class="text-center py-8">
        <div class="text-gray-400 text-lg mb-2">
          <QuestionMarkCircleIcon class="w-8 h-8 mx-auto" aria-hidden="true" />
        </div>
        <p class="text-gray-600">No hay preguntas disponibles para esta categoría.</p>
      </div>
    </div>

    <!-- Contact CTA -->
    <GradientCTA
      class="mt-12"
      title="¿No encuentras tu respuesta?"
      description="Nuestros especialistas en derecho digital están aquí para ayudarte"
      heading-tag="h3"
    >
      <button
        @click="$emit('request-quote')"
        class="inline-flex items-center gap-2 px-8 py-4 bg-white text-accent-600 font-bold rounded-full hover:bg-white/90 transition-colors shadow-lg hover:shadow-xl"
      >
        Contratar Servicio
      </button>
      <button
        @click="router.push(LEGAL_ROUTES.CONTACT)"
        class="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-colors"
      >
        Contactar Directamente
      </button>
    </GradientCTA>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import Button from '@/components/ui/Button.vue'
import GradientCTA from '@/components/ui/GradientCTA.vue'
import { LEGAL_ROUTES } from '@/utils/constants/navigation'
import { useSanitizeHtml } from '@/composables/useSanitizeHtml'

// Import icon mappings for professional appearance
import { uiIcons, actionIcons, statusIcons } from '@/utils/iconMap.js'
import {
  QuestionMarkCircleIcon,
  LockClosedIcon,
  CreditCardIcon,
  CogIcon,
  ScaleIcon
} from '@heroicons/vue/24/outline'

// ✅ CRIT-002 FIX: XSS Protection Composable (ADR-154)
const { sanitizeHtml } = useSanitizeHtml()

const router = useRouter()

// Emits
const emit = defineEmits(['request-quote'])

// Icon mapping function
const getCategoryIcon = (categoryKey) => {
  const iconMap = {
    general: QuestionMarkCircleIcon,
    gdpr: LockClosedIcon,
    pricing: CreditCardIcon,
    process: CogIcon,
    legal: ScaleIcon,
    technical: CogIcon  // CogIcon (reusing for technical)
  }
  return iconMap[categoryKey] || QuestionMarkCircleIcon // Fallback
}

// Reactive state
const activeCategory = ref('general')
const openFAQs = ref(['general-1']) // Pre-open first FAQ

// FAQ Categories
const faqCategories = computed(() => [
  { id: 'general', name: 'General', icon: 'general' },
  { id: 'gdpr', name: 'GDPR', icon: 'gdpr' },
  { id: 'pricing', name: 'Precios', icon: 'pricing' },
  { id: 'process', name: 'Proceso', icon: 'process' },
  { id: 'legal', name: 'Legal', icon: 'legal' },
  { id: 'technical', name: 'Técnico', icon: 'technical' }
])

// FAQ Data - Following MVP_DEFINITION.md service requirements
const faqData = computed(() => [
  // General FAQs
  {
    id: 'general-1',
    category: 'general',
    question: '¿Qué servicios ofrece Olvidadme?',
    answer: `
      <p>Ofrecemos tres servicios principales especializados en derecho digital:</p>
      <ul>
        <li><strong>Derecho al Olvido (499€):</strong> Eliminación de datos personales de buscadores y plataformas online para personas vivas</li>
        <li><strong>Derecho al Olvido Post-Mortem (699€):</strong> Eliminación de datos personales de buscadores y plataformas online de personas fallecidas</li>
        <li><strong>Testamento Digital (599€):</strong> Planificación del legado digital con instrucciones para albacea</li>
      </ul>
      <p>Todos nuestros servicios cumplen con GDPR y la legislación española, incluyendo derechos forales.</p>
    `
  },
  {
    id: 'general-2',
    category: 'general',
    question: '¿Cuánto tiempo tarda el proceso?',
    answer: `
      <p>Los tiempos varían según el servicio y la complejidad:</p>
      <ul>
        <li><strong>Derecho al Olvido:</strong> 3-8 semanas (tramitación completa)</li>
        <li><strong>Derecho al Olvido Post-Mortem:</strong> 4-12 semanas (incluye documentación adicional)</li>
        <li><strong>Testamento Digital:</strong> 2-4 semanas (elaboración del documento)</li>
      </ul>
      <p>Te mantenemos informado en cada paso del proceso.</p>
    `
  },
  {
    id: 'general-3',
    category: 'general',
    question: '¿En qué se diferencia de hacerlo yo mismo?',
    answer: `
      <p>Nuestros especialistas aportan:</p>
      <ul>
        <li><strong>Expertise legal:</strong> Conocimiento actualizado de GDPR y legislación española</li>
        <li><strong>Eficiencia:</strong> Cartas especializadas con mayor tasa de éxito</li>
        <li><strong>Seguimiento:</strong> Gestión completa hasta obtener resultados</li>
        <li><strong>Recursos legales:</strong> Capacidad de escalamiento a tribunales si es necesario</li>
        <li><strong>Compliance:</strong> Garantizamos el cumplimiento de todos los requisitos legales</li>
      </ul>
    `
  },

  // GDPR FAQs
  {
    id: 'gdpr-1',
    category: 'gdpr',
    question: '¿Qué es el derecho al olvido según GDPR?',
    answer: `
      <p>El derecho al olvido (GDPR Art. 17) permite solicitar la eliminación de datos personales cuando:</p>
      <ul>
        <li>Los datos ya no son necesarios para el propósito original</li>
        <li>Se retira el consentimiento y no existe otra base legal</li>
        <li>Los datos han sido tratados ilícitamente</li>
        <li>La eliminación es necesaria para cumplir una obligación legal</li>
      </ul>
      <p>En España, también se aplica la LOPDGDD que refuerza estos derechos.</p>
    `,
    relatedLinks: [
      {
        text: 'GDPR Artículo 17 - Derecho de supresión',
        url: 'https://gdpr-info.eu/art-17-gdpr/'
      },
      {
        text: 'Guía AEPD sobre derecho al olvido',
        url: 'https://www.aepd.es/es/derechos-y-deberes/conoce-tus-derechos/derecho-de-supresion'
      }
    ]
  },
  {
    id: 'gdpr-2',
    category: 'gdpr',
    question: '¿Qué datos se pueden eliminar?',
    answer: `
      <p>Se pueden eliminar diversos tipos de datos personales:</p>
      <ul>
        <li><strong>Información básica:</strong> Nombre, email, datos de contacto, dirección</li>
        <li><strong>Contenido generado:</strong> Publicaciones, comentarios, reseñas</li>
        <li><strong>Imágenes:</strong> Fotografías, videos donde apareces</li>
        <li><strong>Datos profesionales:</strong> Información laboral desactualizada</li>
        <li><strong>Registros públicos:</strong> Algunos registros en directorios online</li>
      </ul>
      <p><strong>Limitaciones:</strong> No se pueden eliminar datos necesarios para libertad de expresión, cumplimiento legal o interés público.</p>
    `
  },

  // Pricing FAQs
  {
    id: 'pricing-1',
    category: 'pricing',
    question: '¿Por qué hay diferentes planes de precios?',
    answer: `
      <p>Los servicios se adaptan a diferentes necesidades:</p>
      <ul>
        <li><strong>Derecho al Olvido (499€):</strong> Para personas vivas que quieren eliminar sus datos de internet</li>
        <li><strong>Derecho al Olvido Post-Mortem (699€):</strong> Para familiares que gestionan datos de personas fallecidas</li>
        <li><strong>Testamento Digital (599€):</strong> Para planificar tu legado digital mientras vives</li>
      </ul>
      <p>Todos los precios incluyen IVA y sin costes ocultos. Modelo: 1 pago = 1 caso.</p>
    `
  },
  {
    id: 'pricing-2',
    category: 'pricing',
    question: '¿Hay costes adicionales?',
    answer: `
      <p><strong>No hay costes ocultos.</strong> El precio que pagas incluye todo lo especificado en cada servicio:</p>
      <ul>
        <li><strong>Derecho al Olvido:</strong> Análisis + tramitación de hasta 5 URLs + seguimiento hasta respuesta</li>
        <li><strong>Derecho al Olvido Post-Mortem:</strong> Gestión completa + hasta 5 URLs + documentación legal</li>
        <li><strong>Testamento Digital:</strong> Elaboración del documento + designación de albacea + instrucciones detalladas</li>
      </ul>
      <p><strong>Única excepción:</strong> En casos extraordinarios que requieran procedimiento judicial, se consultaría previamente cualquier coste adicional.</p>
    `
  },
  {
    id: 'pricing-3',
    category: 'pricing',
    question: '¿Ofrecen garantía de resultados?',
    answer: `
      <p>Sí. Si tras el análisis previo de viabilidad consideramos que tu caso no reúne los requisitos legales necesarios para tramitar la solicitud conforme al artículo 17 del RGPD, te devolvemos el 100 % del importe. Para los servicios Post-Mortem y Testamento Digital, garantizamos gestión profesional completa con documentos legalmente válidos.</p>
    `
  },

  // Process FAQs
  {
    id: 'process-1',
    category: 'process',
    question: '¿Cómo funciona el proceso paso a paso?',
    answer: `
      <p>El proceso estándar incluye estas fases:</p>
      <ol>
        <li><strong>Análisis inicial:</strong> Evaluamos tu caso y viabilidad legal</li>
        <li><strong>Documentación:</strong> Recopilamos evidencias y preparamos documentación legal</li>
        <li><strong>Envío de solicitudes:</strong> Contactamos oficialmente con las plataformas</li>
        <li><strong>Seguimiento:</strong> Monitorizamos respuestas y escalamos si es necesario</li>
        <li><strong>Resolución:</strong> Confirmamos eliminación o informamos de alternativas legales</li>
      </ol>
      <p>Te mantenemos informado en cada etapa con actualizaciones regulares.</p>
    `
  },
  {
    id: 'process-2',
    category: 'process',
    question: '¿Qué información necesitan de mí?',
    answer: `
      <p>La información requerida varía según el servicio:</p>
      <ul>
        <li><strong>Datos básicos:</strong> Nombre, email, datos de contacto, residencia (para jurisdicción)</li>
        <li><strong>Identificación:</strong> DNI o NIE (encriptado y protegido según GDPR)</li>
        <li><strong>Detalles del caso:</strong> URLs específicas, capturas de pantalla, contexto</li>
        <li><strong>Documentación adicional:</strong> En casos post-mortem, certificado de defunción y relación</li>
      </ul>
      <p><strong>Seguridad:</strong> Todos los datos se encriptan con AES-256 y se eliminarán según nuestra política de retención (30 días tras resolución).</p>
    `
  },

  // Legal FAQs
  {
    id: 'legal-1',
    category: 'legal',
    question: '¿Qué pasa si las plataformas se niegan?',
    answer: `
      <p>Si una plataforma rechaza la solicitud inicial:</p>
      <ul>
        <li><strong>Derecho al Olvido:</strong> Analizamos el rechazo y preparamos segunda solicitud o recurso</li>
        <li><strong>Derecho al Olvido Post-Mortem:</strong> Evaluamos opciones legales y podemos proceder con:</li>
        <ul>
          <li>Reclamación ante AEPD</li>
          <li>Procedimiento administrativo</li>
          <li>Acción judicial si procede</li>
        </ul>
        <li><strong>Testamento Digital:</strong> Se incluye asesoramiento sobre las opciones disponibles</li>
      </ul>
      <p>Cada rechazo se analiza individualmente para determinar la mejor estrategia legal.</p>
    `
  },
  {
    id: 'legal-2',
    category: 'legal',
    question: '¿Pueden rechazar legalmente mi solicitud?',
    answer: `
      <p>Sí, existen excepciones legítimas al derecho de eliminación:</p>
      <ul>
        <li><strong>Libertad de expresión e información:</strong> Noticias de interés público</li>
        <li><strong>Cumplimiento de obligación legal:</strong> Registros requeridos por ley</li>
        <li><strong>Interés público:</strong> Información relevante para la sociedad</li>
        <li><strong>Intereses legítimos:</strong> Del responsable que prevalezcan sobre los tuyos</li>
        <li><strong>Ejercicio de derechos:</strong> En procedimientos judiciales</li>
      </ul>
      <p>Nuestro análisis previo evalúa estas posibilidades para maximizar las opciones de éxito.</p>
    `
  },

  // Technical FAQs
  {
    id: 'technical-1',
    category: 'technical',
    question: '¿Cómo protegen mis datos personales?',
    answer: `
      <p>Implementamos seguridad de nivel bancario:</p>
      <ul>
        <li><strong>Encriptación AES-256:</strong> Todos los datos sensibles encriptados</li>
        <li><strong>Transmisión segura:</strong> HTTPS y TLS 1.3 en todas las comunicaciones</li>
        <li><strong>Almacenamiento temporal:</strong> Datos eliminados automáticamente tras 30 días</li>
        <li><strong>Acceso restringido:</strong> Solo personal autorizado y con auditoría completa</li>
        <li><strong>Cumplimiento GDPR:</strong> Certificación y auditorías regulares</li>
      </ul>
      <p>Puedes solicitar eliminación de tus datos en cualquier momento.</p>
    `
  },
  {
    id: 'technical-2',
    category: 'technical',
    question: '¿Qué plataformas cubren?',
    answer: `
      <p>Trabajamos con las principales plataformas y buscadores:</p>
      <ul>
        <li><strong>Buscadores:</strong> Google, Bing, Yahoo, DuckDuckGo</li>
        <li><strong>Redes sociales:</strong> Facebook, Instagram, Twitter/X, LinkedIn, TikTok</li>
        <li><strong>Plataformas profesionales:</strong> InfoJobs, directorios empresariales</li>
        <li><strong>Medios online:</strong> Periódicos, blogs, portales de noticias</li>
        <li><strong>Otros servicios:</strong> YouTube, Vimeo, foros, directorios locales</li>
      </ul>
      <p>Si tu caso involucra una plataforma no listada, evaluamos la viabilidad caso por caso.</p>
    `
  }
])

// Computed
const filteredFAQs = computed(() => {
  return faqData.value.filter(faq => faq.category === activeCategory.value)
})

// Methods
const setActiveCategory = (categoryId) => {
  activeCategory.value = categoryId
  // Auto-open first FAQ of new category
  const firstFAQ = filteredFAQs.value[0]
  if (firstFAQ) {
    openFAQs.value = [firstFAQ.id]
  }
}

const toggleFAQ = (faqId) => {
  const index = openFAQs.value.indexOf(faqId)
  if (index > -1) {
    openFAQs.value.splice(index, 1)
  } else {
    openFAQs.value.push(faqId)
  }
}

</script>

<style scoped>
@reference "../../assets/styles/main.css";

.service-faq {
  @apply w-full;
}

/* FAQ Item animations */
.faq-item {
  @apply transition-all duration-200 ease-in-out;
}

.faq-item:hover {
  @apply shadow-sm border-gray-300;
}

/* Prose content styling */
.prose ul {
  @apply list-disc pl-5 space-y-1;
}

.prose ol {
  @apply list-decimal pl-5 space-y-1;
}

.prose li {
  @apply text-sm;
}

.prose strong {
  @apply font-semibold text-gray-900;
}

.prose p {
  @apply mb-3 text-sm;
}

.prose p:last-child {
  @apply mb-0;
}

/* Category button active state */
.service-faq .bg-teal-500 {
  @apply text-white;
}

/* Smooth expand/collapse animation */
.faq-item > div:last-child {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Contact CTA styling */
.service-faq .bg-white\/80 {
  @apply transition-all duration-300;
}

.service-faq .bg-white\/80:hover {
  @apply shadow-md;
}
</style>